/** App Store metadata field specifications: labels, limits, editing surface. */

export type InfoField = 'name' | 'subtitle' | 'privacyPolicyUrl' | 'privacyChoicesUrl';
export type VersionField =
  | 'description'
  | 'keywords'
  | 'promotionalText'
  | 'whatsNew'
  | 'supportUrl'
  | 'marketingUrl';

export type MetadataField = InfoField | VersionField;

export interface FieldSpec {
  field: MetadataField;
  target: 'info' | 'version';
  label: string;
  maxLength: number;
  multiline: boolean;
  kind: 'text' | 'keywords' | 'url';
  help?: string;
  /** Field may be translated/generated by AI. URLs are excluded. */
  aiEligible: boolean;
}

export const FIELD_SPECS: Record<MetadataField, FieldSpec> = {
  name: {
    field: 'name',
    target: 'info',
    label: 'App Name',
    maxLength: 30,
    multiline: false,
    kind: 'text',
    help: 'Shown on the App Store and on devices. Must be unique across the App Store.',
    aiEligible: true,
  },
  subtitle: {
    field: 'subtitle',
    target: 'info',
    label: 'Subtitle',
    maxLength: 30,
    multiline: false,
    kind: 'text',
    help: 'A brief tagline shown under the app name.',
    aiEligible: true,
  },
  privacyPolicyUrl: {
    field: 'privacyPolicyUrl',
    target: 'info',
    label: 'Privacy Policy URL',
    maxLength: 255,
    multiline: false,
    kind: 'url',
    help: 'Publicly accessible privacy policy. Required by Apple for every app.',
    aiEligible: false,
  },
  privacyChoicesUrl: {
    field: 'privacyChoicesUrl',
    target: 'info',
    label: 'Privacy Choices URL',
    maxLength: 255,
    multiline: false,
    kind: 'url',
    help: 'Optional page where users can manage their privacy choices.',
    aiEligible: false,
  },
  description: {
    field: 'description',
    target: 'version',
    label: 'Description',
    maxLength: 4000,
    multiline: true,
    kind: 'text',
    aiEligible: true,
  },
  keywords: {
    field: 'keywords',
    target: 'version',
    label: 'Keywords',
    maxLength: 100,
    multiline: false,
    kind: 'keywords',
    help: 'Comma-separated. The 100-character limit includes the commas.',
    aiEligible: true,
  },
  promotionalText: {
    field: 'promotionalText',
    target: 'version',
    label: 'Promotional Text',
    maxLength: 170,
    multiline: true,
    kind: 'text',
    help: 'The only field Apple lets you update on a live version without a new release.',
    aiEligible: true,
  },
  whatsNew: {
    field: 'whatsNew',
    target: 'version',
    label: "What's New",
    maxLength: 4000,
    multiline: true,
    kind: 'text',
    help: 'Release notes. Apple starts them empty on every new version (nothing carries over) and doesn’t accept them on an app’s first version.',
    aiEligible: true,
  },
  supportUrl: {
    field: 'supportUrl',
    target: 'version',
    label: 'Support URL',
    maxLength: 255,
    multiline: false,
    kind: 'url',
    aiEligible: false,
  },
  marketingUrl: {
    field: 'marketingUrl',
    target: 'version',
    label: 'Marketing URL',
    maxLength: 255,
    multiline: false,
    kind: 'url',
    aiEligible: false,
  },
};

export const INFO_FIELDS: InfoField[] = ['name', 'subtitle', 'privacyPolicyUrl', 'privacyChoicesUrl'];
export const VERSION_FIELDS: VersionField[] = [
  'promotionalText',
  'description',
  'keywords',
  'whatsNew',
  'supportUrl',
  'marketingUrl',
];
export const ALL_FIELDS: MetadataField[] = [...INFO_FIELDS, ...VERSION_FIELDS];

/**
 * Detect unmistakable internal placeholder values without rejecting legitimate
 * store copy. Locale tags and long numbers are both valid in descriptions (for
 * language support, model numbers, case IDs, PINs, etc.), so they are only unsafe
 * when combined as a generated locale/ID token or when the whole value is an ID.
 * URLs are validated separately because identifiers are normal inside URLs.
 */
export function looksLikeMetadataPlaceholder(value: string): boolean {
  const uuid = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/i;
  const localeThenId = /\b[a-z]{2}-(?:[a-z]{2}|hans|hant)[\s:._-]{0,4}\d{6,}\b/i;
  const idThenLocale = /\b\d{6,}[\s:._-]{0,4}[a-z]{2}-(?:[a-z]{2}|hans|hant)\b/i;
  const trailingLocaleId = /(?:^|\s)[a-z]{2}-\d{6,}\s*$/i;
  const standaloneLabeledId = /^\s*(?:(?:internal|app|apple|build|release|locale|version)\s*)?(?:id\s*)?[:#._-]*\s*\d{6,}\s*$/i;
  return uuid.test(value) || localeThenId.test(value) || idThenLocale.test(value) || trailingLocaleId.test(value) || standaloneLabeledId.test(value);
}

export function validateFieldValue(field: MetadataField, value: string): string | null {
  const spec = FIELD_SPECS[field];
  if (value.length > spec.maxLength) {
    return `${spec.label} exceeds ${spec.maxLength} characters (currently ${value.length}).`;
  }
  if (spec.kind === 'url' && value.trim() !== '') {
    // Keep shared/ free of DOM/Node lib types — a light check is enough; Apple validates authoritatively.
    if (!/^https?:\/\/[^\s]+\.[^\s]{2,}/i.test(value.trim())) {
      return 'Must be a valid http(s) URL.';
    }
  }
  if (spec.kind !== 'url' && looksLikeMetadataPlaceholder(value)) {
    return `${spec.label} contains an internal locale code, identifier, or placeholder. Replace it with clean customer-facing ${spec.kind === 'keywords' ? 'keywords' : 'text'} in the selected language.`;
  }
  return null;
}
